import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Checkbox } from "./components/ui/checkbox";
import { Folder, File, Download, Trash2, FolderPlus, ArrowLeft, Home, Eye } from 'lucide-react';
import { saveAs } from 'file-saver';
import { DndContext, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';

const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_URL;
  if (env && env.trim().length > 0) {
    const trimmed = env.trim().replace(/\/+$/, '');
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return 'http://127.0.0.1:8000';
  }
  return '/api';
};
const API_URL = resolveApiBase();
const PAGE_SIZE = 200;

const DraggableItem = ({ id, data, children }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: id,
    data: data,
  });
  
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative'
  } : {
    cursor: 'grab'
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
};

const DroppableFolder = ({ id, folderKey, children, className }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: id,
    data: { type: 'folder', key: folderKey },
  });

  return (
    <div 
        ref={setNodeRef} 
        className={`${className} ${isOver ? 'bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-500 rounded' : ''}`}
    >
      {children}
    </div>
  );
};

// Helpers defined outside so we can use them during lazy init
const normalizePrefix = (p) => {
  if (!p) return "";
  let s = (p || "").toString();
  s = s.replace(/^\/+/, ""); // no leading slashes
  // if it's not empty, keep it folder-like by ensuring trailing slash
  if (s.length > 0 && !s.endsWith("/")) s += "/";
  return s;
};

const getCleanPathForUrl = (p) => {
  return (p || "").toString().replace(/^\/+/, "").replace(/\/+$/, "");
};

const FileManager = ({ token }) => {
  const [items, setItems] = useState({ folders: [], files: [] });
  
  // Lazy initialization of currentPath from the URL query param.
  // This ensures we start with the correct path immediately on mount,
  // preventing a flash of root content or "empty path" race conditions.
  const [currentPath, setCurrentPath] = useState(() => {
    if (typeof window === 'undefined') return '';
    const qs = new URLSearchParams(window.location.search);
    const raw = qs.get("path");
    return normalizePrefix(raw);
  });

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [dragActiveItem, setDragActiveItem] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState(null); // {exists, run}
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyDetail, setVerifyDetail] = useState(null); // {run, items}
  const [verifyDetailOpen, setVerifyDetailOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Listen for browser navigation (Back/Forward buttons)
  // When the URL changes externally (popstate), we update currentPath.
  useEffect(() => {
    const qs = new URLSearchParams(location.search || "");
    const raw = qs.get("path") || "";
    // URLSearchParams handles decoding
    const next = normalizePrefix(raw);
    setCurrentPath((prev) => (normalizePrefix(prev) === next ? prev : next));
  }, [location.search]);

  // Keep URL query (?path=...) in sync with state (when user clicks folders)
  useEffect(() => {
    const clean = getCleanPathForUrl(currentPath);
    const qs = new URLSearchParams(location.search || "");
    const existing = (qs.get("path") || "").toString();
    const desired = clean ? clean : "";
    
    // Only navigate if the URL doesn't match the state
    if ((existing || "") !== (desired || "")) {
      const nextQs = new URLSearchParams(location.search || "");
      if (desired) nextQs.set("path", desired);
      else nextQs.delete("path");
      const next = nextQs.toString();
      navigate(next ? `/files?${next}` : "/files", { replace: false }); // replace: false allows back button history
    }
  }, [currentPath, location.search, navigate]);

  const selectedFiles = items.files.filter((f) => selectedItems.has(f.key));
  const selectedFolders = items.folders.filter((f) => selectedItems.has(f.key));

  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [currentPath]);

  const totalItems = items.folders.length + items.files.length;
  const pageCount = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, totalItems);
  // Folders come first, so a page may contain folders, files, or both.
  const pageFolders = items.folders.slice(pageStart, pageEnd);
  const pageFiles = items.files.slice(
    Math.max(0, pageStart - items.folders.length),
    Math.max(0, pageEnd - items.folders.length)
  );
  const pageKeys = [...pageFolders.map((f) => f.key), ...pageFiles.map((f) => f.key)];

  const getDisplayPath = () => {
    if (!currentPath) return '/';
    const clean = currentPath.replace(/\/+$/, '');
    return `/${clean}`;
  };

  const shareFolderUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const base = window.location.origin || "";
    const clean = getCleanPathForUrl(currentPath);
    return clean ? `${base}/files?path=${encodeURIComponent(clean)}` : `${base}/files`;
  }, [currentPath]);

  const handleCopyShareUrl = async () => {
    if (!shareFolderUrl) return;
    try {
      await navigator.clipboard.writeText(shareFolderUrl);
    } catch {
      try {
        window.prompt("Copy this URL:", shareFolderUrl);
      } catch {}
    }
  };

  const isAmazonBusinessFolder = useCallback(() => {
    // New layout: Nestos Shop Ltd - Invoice storage/amazon.com_business_orders/{country}/{month}/
    const p = (currentPath || '').toString();
    if (!p.startsWith('Nestos Shop Ltd - Invoice storage/amazon.com_business_orders/')) return false;
    const rest = p.replace('Nestos Shop Ltd - Invoice storage/amazon.com_business_orders/', '');
    const parts = rest.split('/').filter(Boolean);
    return parts.length >= 2;
  }, [currentPath]);

  const fetchVerifyStatus = useCallback(async () => {
    if (!token) return;
    if (!isAmazonBusinessFolder()) {
      setVerifyStatus(null);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/verify/amazon-business/status`, {
        params: { prefix: currentPath },
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerifyStatus(res.data || null);
    } catch (e) {
      // non-fatal
      setVerifyStatus(null);
    }
  }, [token, currentPath, isAmazonBusinessFolder]);

  const fetchFiles = useCallback(async (path) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/files/`, {
        params: { prefix: path },
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
      setError('');
      setSelectedItems(new Set()); // Clear selection on navigate
    } catch (err) {
      console.error("Error fetching files:", err);
      setError('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchFiles(currentPath);
  }, [fetchFiles, currentPath]);

  useEffect(() => {
    fetchVerifyStatus();
  }, [fetchVerifyStatus]);

  const handleNavigate = (path) => {
    setCurrentPath(path);
  };

  const handleNavigateUp = () => {
    if (!currentPath) return;
    const parts = currentPath.split('/').filter(p => p);
    parts.pop();
    const newPath = parts.length > 0 ? parts.join('/') + '/' : '';
    setCurrentPath(newPath);
  };

  const handleBreadcrumbClick = (index, parts) => {
    const newPath = parts.slice(0, index + 1).join('/') + '/';
    setCurrentPath(newPath);
  };

  const handleCreateFolder = async () => {
    const folderName = window.prompt("Enter folder name:");
    if (!folderName) return;

    // Sanitize folder name - no slashes allowed in name itself
    const sanitizedName = folderName.replace(/\//g, '');
    const newFolderPath = currentPath + sanitizedName + '/';

    try {
      await axios.post(`${API_URL}/files/folder`, 
        { path: newFolderPath },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchFiles(currentPath);
    } catch (err) {
      console.error("Error creating folder:", err);
      setError('Failed to create folder');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', currentPath);

    try {
      await axios.post(`${API_URL}/files/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      await fetchFiles(currentPath);
      setError('');
    } catch (err) {
      console.error("Error uploading file:", err);
      setError('Failed to upload file');
    } finally {
      setUploading(false);
      event.target.value = null;
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Are you sure you want to delete ${key}? This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API_URL}/files/${key}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchFiles(currentPath);
    } catch (err) {
      console.error("Error deleting item:", err);
      setError('Failed to delete item');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.size} items? This action cannot be undone.`)) return;
    
    setLoading(true);
    setInfo('');
    try {
        const res = await axios.post(`${API_URL}/files/bulk-delete`,
            { keys: Array.from(selectedItems) },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        const { deleted = 0, errors = [] } = res.data || {};
        await fetchFiles(currentPath);
        setSelectedItems(new Set());
        if (errors.length > 0) {
          const lines = errors.slice(0, 8).map((e) => `- ${e.key}: ${e.message}`).join('\n');
          setError(`${deleted} deleted, ${errors.length} failed:\n${lines}`);
        }
    } catch (err) {
        console.error("Error deleting items:", err);
        setError('Failed to delete some items');
    } finally {
        setLoading(false);
    }
  };

  const handleMoveItem = async (sourceKey, destinationFolderKey) => {
    // Determine the destination key
    // If destinationFolderKey is empty string, it's root
    // sourceKey is the full path of the file being moved
    // destinationFolderKey is the full path of the folder we are dropping into
    
    const fileName = sourceKey.split('/').filter(p=>p).pop();
    // If it's a folder being moved, logic is more complex (recursive move/rename).
    // For now, let's assume we only move FILES.
    if (sourceKey.endsWith('/')) {
        alert("Moving folders is not supported yet.");
        return;
    }

    const destinationKey = destinationFolderKey + fileName;

    if (sourceKey === destinationKey) return;

    if (!window.confirm(`Move ${fileName} to ${destinationFolderKey || 'root'}?`)) return;

    try {
        await axios.post(`${API_URL}/files/move`, {
            source_key: sourceKey,
            destination_key: destinationKey
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        await fetchFiles(currentPath);
    } catch (err) {
        console.error("Error moving file:", err);
        setError('Failed to move file');
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setDragActiveItem(null);

    if (!over) return;

    const sourceKey = active.id;
    const targetKey = over.id; // This will be the folder key
    
    // Check if we dropped onto a folder
    if (over.data.current?.type === 'folder' && sourceKey !== targetKey) {
        handleMoveItem(sourceKey, targetKey);
    }
  };

  const handleDragStart = (event) => {
      setDragActiveItem(event.active.data.current);
  };

  const handleSelectionChange = (key, isSelected) => {
    const newSelection = new Set(selectedItems);
    if (isSelected) {
      newSelection.add(key);
    } else {
      newSelection.delete(key);
    }
    setSelectedItems(newSelection);
  };

  const handleSelectPage = (isChecked) => {
    const newSelection = new Set(selectedItems);
    pageKeys.forEach((key) => (isChecked ? newSelection.add(key) : newSelection.delete(key)));
    setSelectedItems(newSelection);
  };

  const handleSelectAllInFolder = () => {
    setSelectedItems(new Set([...items.folders.map(f => f.key), ...items.files.map(f => f.key)]));
  };

  const handleDownload = async () => {
    if (selectedItems.size === 0) return;

    setLoading(true);
    setError('');
    setInfo('');

    // Single file download
    if (selectedItems.size === 1 && selectedFiles.length === 1 && selectedFolders.length === 0) {
      const file = selectedFiles[0];
      try {
        const urlRes = await axios.get(`${API_URL}/files/${file.key}/url`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const url = urlRes.data.url;
        const contentRes = await axios.get(url, { responseType: 'blob' });
        saveAs(contentRes.data, file.name);
      } catch (err) {
        console.error("Error downloading file:", err);
        setError("Failed to download file.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Bulk download: the server streams the ZIP, the browser saves it natively.
    if (selectedFiles.length === 0 && selectedFolders.length === 0) {
      setLoading(false);
      return;
    }
    const singleFolder = selectedFolders.length === 1 && selectedFiles.length === 0;
    const folderName = currentPath ? currentPath.split('/').filter(p=>p).pop() : 'root';
    const payload = {
      keys: [...selectedFolders.map((f) => f.key), ...selectedFiles.map((f) => f.key)],
      root_prefix: singleFolder ? selectedFolders[0].key : currentPath,
      zip_name: singleFolder ? `${selectedFolders[0].name}.zip` : `${folderName}_download.zip`,
    };

    try {
      submitZipDownload(payload);
      setInfo(`ZIP download started (${selectedItems.size} items). Your browser will show its progress.`);
    } catch (err) {
      console.error("Error starting zip download:", err);
      setError("Failed to download zip.");
    } finally {
      setLoading(false);
    }
  };

  const submitZipDownload = (payload) => {
    // A hidden iframe target keeps the page in place; the attachment response becomes a download.
    const frameName = 'zip-download-frame';
    let frame = document.querySelector(`iframe[name="${frameName}"]`);
    if (!frame) {
      frame = document.createElement('iframe');
      frame.name = frameName;
      frame.style.display = 'none';
      document.body.appendChild(frame);
    }
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${API_URL}/files/zip`;
    form.target = frameName;
    form.style.display = 'none';
    const addField = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    addField('token', token);
    addField('payload', JSON.stringify(payload));
    document.body.appendChild(form);
    form.submit();
    form.remove();
  };

  const handleView = async (fileKey) => {
    try {
      const urlRes = await axios.get(`${API_URL}/files/${fileKey}/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = urlRes.data.url;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error("Error viewing file:", err);
      // Fallback: try direct S3 URL if public or just alert
      setError("Failed to open file.");
    }
  };

  const handleVerify = async () => {
    if (!token) return;
    if (!isAmazonBusinessFolder()) return;
    setVerifyLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/verify/amazon-business/run`,
        { prefix: currentPath },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const runId = res.data?.run_id;
      await fetchVerifyStatus();
      // Poll until completed/failed (or up to ~2h)
      const startedAt = Date.now();
      while (Date.now() - startedAt < 2 * 60 * 60 * 1000) {
        const st = await axios.get(`${API_URL}/verify/amazon-business/status`, {
          params: { prefix: currentPath },
          headers: { Authorization: `Bearer ${token}` }
        });
        setVerifyStatus(st.data || null);
        const run = st.data?.run;
        const status = run?.status;
        if (status === 'completed' || status === 'failed' || status === 'cancelled') break;
        await new Promise(r => setTimeout(r, 2500));
      }
      if (runId) {
        // preload details optionally
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Verify failed');
    } finally {
      setVerifyLoading(false);
    }
  };

  const openVerifyInfo = async () => {
    if (!token) return;
    const runId = verifyStatus?.run?.id;
    if (!runId) return;
    setVerifyDetailOpen(true);
    try {
      const res = await axios.get(`${API_URL}/verify/amazon-business/runs/${runId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerifyDetail(res.data || null);
    } catch (e) {
      setVerifyDetail({ error: e.response?.data?.detail || e.message || 'Failed to load verify info' });
    }
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderBreadcrumbs = () => {
    const parts = currentPath.split('/').filter(p => p);
    
    // Determine the parent folder path for drag-to-up
    // currentPath is like 'a/b/c/'
    // Parent is 'a/b/'
    let parentPath = '';
    if (parts.length > 0) {
        const parentParts = [...parts];
        parentParts.pop();
        parentPath = parentParts.length > 0 ? parentParts.join('/') + '/' : '';
    }

    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        {currentPath !== '' && (
            <DroppableFolder id={parentPath === '' ? 'root-droppable' : parentPath} folderKey={parentPath} className="inline-block p-1">
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className="p-1 h-auto"
                    onClick={() => handleNavigateUp()}
                    title="Drag files here to move up"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Up
                </Button>
            </DroppableFolder>
        )}
        
        <Button 
            variant="ghost" 
            size="sm" 
            className="p-1 h-auto"
            onClick={() => handleNavigate('')}
        >
            <Home className="h-4 w-4" />
        </Button>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            <span>/</span>
            <Button 
                variant="ghost" 
                size="sm" 
                className="p-1 h-auto hover:underline"
                onClick={() => handleBreadcrumbClick(index, parts)}
            >
                {part}
            </Button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const allSelected = totalItems > 0 && selectedItems.size === totalItems;
  const pageSelected = pageKeys.length > 0 && pageKeys.every((key) => selectedItems.has(key));

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="space-y-1">
              <h1 className="text-3xl font-bold tracking-tight">File Manager</h1>
              <div className="text-sm text-muted-foreground">
                {items.folders.length} folders / {items.files.length} files /{" "}
                {items.files.filter((f) => (f?.name || "").toLowerCase().endsWith(".pdf")).length} PDFs
              </div>
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <Button variant="outline" onClick={handleCreateFolder} className="w-full sm:w-auto">
                    <FolderPlus className="mr-2 h-4 w-4" />
                    New Folder
                </Button>

                {selectedItems.size > 0 && (
                    <Button variant="destructive" onClick={handleBulkDelete}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete ({selectedItems.size})
                    </Button>
                )}

                <Button variant="outline" onClick={handleDownload} disabled={selectedItems.size === 0} className="w-full sm:w-auto">
                    <Download className="mr-2 h-4 w-4" />
                    {selectedItems.size === 1 && selectedFiles.length === 1 && selectedFolders.length === 0
                      ? 'Download'
                      : `Download Zip (${selectedItems.size})`}
                </Button>

                {isAmazonBusinessFolder() && (
                  <>
                    <Button
                      variant={verifyStatus?.exists ? "secondary" : "default"}
                      onClick={handleVerify}
                      disabled={verifyLoading || loading}
                      className="w-full sm:w-auto"
                    >
                      {verifyLoading ? "Verifying..." : (verifyStatus?.exists ? "Re-verify" : "Verify")}
                    </Button>
                    {verifyStatus?.exists && verifyStatus?.run && (
                      <Button
                        variant="outline"
                        onClick={openVerifyInfo}
                        disabled={verifyLoading}
                        className="w-full sm:w-auto"
                      >
                        Info
                      </Button>
                    )}
                  </>
                )}

            <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
            />
            <label htmlFor="file-upload" className="w-full sm:w-auto">
                <Button asChild disabled={uploading} className="cursor-pointer w-full sm:w-auto">
                <span>{uploading ? 'Uploading...' : 'Upload File'}</span>
                </Button>
            </label>
            </div>
        </div>

        {error && (
            <div className="bg-destructive/15 text-destructive px-4 py-2 rounded-md">
            {error}
            </div>
        )}

        {info && (
          <div className="bg-amber-500/10 text-amber-700 dark:text-amber-200 px-4 py-2 rounded-md whitespace-pre-wrap text-sm border border-amber-500/20">
            {info}
          </div>
        )}

        {isAmazonBusinessFolder() && verifyStatus?.exists && verifyStatus?.run && (
          <div className="rounded-xl border border-border/60 bg-background/70 p-4 text-sm">
            <div className="font-medium">
              Verified last on{" "}
              {verifyStatus.run.finished_at ? new Date(verifyStatus.run.finished_at).toLocaleString() : "—"}{" "}
              (total {verifyStatus.run.total_files || 0} PDFs, moved {verifyStatus.run.moved_files || 0})
            </div>
            {(verifyStatus.run.moved_files || 0) > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                Click <span className="font-medium">Info</span> to see all moved PDFs.
              </div>
            )}
          </div>
        )}

        {verifyDetailOpen && (
          <div className="rounded-xl border border-border/60 bg-background/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold">Verification details</div>
              <Button variant="outline" size="sm" onClick={() => setVerifyDetailOpen(false)}>Close</Button>
            </div>
            <div className="mt-3 space-y-4 text-sm">
              {!verifyDetail ? (
                <div className="text-muted-foreground">Loading...</div>
              ) : verifyDetail.error ? (
                <div className="text-destructive">{verifyDetail.error}</div>
              ) : (
                (() => {
                  const run = verifyDetail.run || {};
                  const items = Array.isArray(verifyDetail.items) ? verifyDetail.items : [];
                  const getName = (key) => {
                    try { return (key || '').toString().split('/').pop() || key; } catch { return key; }
                  };
                  const movedItems = items.filter((it) => it && it.moved);
                  const errorItems = items.filter((it) => it && it.error);
                  const movedByMonth = movedItems.reduce((acc, it) => {
                    const m = (it.target_month || 'Unknown').toString();
                    acc[m] = acc[m] || [];
                    acc[m].push(it);
                    return acc;
                  }, {});

                  return (
                    <>
                      <div className="space-y-1">
                        <div className="font-medium">
                          Verified last on{" "}
                          {run.finished_at ? new Date(run.finished_at).toLocaleString() : "—"}
                        </div>
                        <div className="text-muted-foreground">
                          Total PDFs: {run.total_files || 0} · Processed: {run.processed_files || 0} ·
                          Moved: {run.moved_files || 0} · Errors: {run.errors_count || 0}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono break-all">
                          Prefix: {run.prefix || ""}
                        </div>
                      </div>

                      {Object.keys(movedByMonth).length > 0 ? (
                        <div className="space-y-2">
                          <div className="font-semibold">Moved files</div>
                          {Object.entries(movedByMonth).map(([month, list]) => (
                            <div key={month} className="rounded-lg border border-border/60 bg-background/60 p-3">
                              <div className="text-sm font-medium">
                                Found {list.length} file(s) that should be in <span className="font-semibold">{month}</span> and moved them.
                              </div>
                              <ol className="mt-2 list-decimal pl-5 text-xs text-muted-foreground">
                                {list.map((it, idx) => (
                                  <li key={`${it.id || idx}`}>
                                    {getName(it.s3_key)} {it.moved_to_key ? <span className="font-mono">→ {it.moved_to_key.split('/').slice(-3).join('/')}</span> : null}
                                    {(() => {
                                      const destName = getName(it.moved_to_key || "");
                                      const isDup = destName.startsWith("duplicate_");
                                      return isDup ? (
                                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                          DUPLICATE
                                        </span>
                                      ) : null;
                                    })()}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted-foreground">No files were moved.</div>
                      )}

                      {errorItems.length > 0 && (
                        <div className="space-y-2">
                          <div className="font-semibold text-rose-500">Errors ({errorItems.length})</div>
                          <ol className="list-decimal pl-5 text-xs text-rose-600">
                            {errorItems.slice(0, 20).map((it, idx) => (
                              <li key={`${it.id || idx}`}>
                                {getName(it.s3_key)}: {it.error}
                              </li>
                            ))}
                            {errorItems.length > 20 && (
                              <li>+ {errorItems.length - 20} more</li>
                            )}
                          </ol>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>
        )}

        <Card>
            <CardHeader className="pb-4">
                {renderBreadcrumbs()}
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground font-mono break-all">
                    Path: {getDisplayPath()}
                  </div>
                  {shareFolderUrl && (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-xs text-muted-foreground font-mono break-all">
                        URL:{" "}
                        <a
                          href={shareFolderUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="underline underline-offset-2"
                        >
                          {shareFolderUrl}
                        </a>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleCopyShareUrl}>
                        Copy URL
                      </Button>
                    </div>
                  )}
                </div>
            </CardHeader>
            <CardContent>
            {loading && !items.folders.length && !items.files.length ? (
                <div className="text-center py-4">Loading...</div>
            ) : (
                <div className="w-full overflow-x-auto">
                {pageCount > 1 && (pageSelected || allSelected) && (
                  <div className="mb-3 flex flex-wrap items-center justify-center gap-2 rounded-md bg-muted px-4 py-2 text-sm">
                    {allSelected ? (
                      <>
                        <span>All {totalItems} items in this folder are selected.</span>
                        <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setSelectedItems(new Set())}>
                          Clear selection
                        </Button>
                      </>
                    ) : (
                      <>
                        <span>All {pageKeys.length} items on this page are selected.</span>
                        <Button variant="link" size="sm" className="h-auto p-0" onClick={handleSelectAllInFolder}>
                          Select all {totalItems} items in this folder
                        </Button>
                      </>
                    )}
                  </div>
                )}
                <Table className="min-w-[720px]">
                <TableHeader>
                    <TableRow>
                    <TableHead className="w-[50px]">
                        <Checkbox 
                            checked={pageSelected}
                            onCheckedChange={handleSelectPage}
                        />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Type</TableHead>
                    <TableHead className="hidden md:table-cell">Size</TableHead>
                    <TableHead className="hidden lg:table-cell">Last Modified</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {/* Folders */}
                    {pageFolders.map((folder) => (
                    <TableRow key={folder.key}>
                        <TableCell>
                            <Checkbox 
                                checked={selectedItems.has(folder.key)}
                                onCheckedChange={(checked) => handleSelectionChange(folder.key, checked)}
                            />
                        </TableCell>
                        <TableCell className="font-medium">
                            <DroppableFolder id={folder.key} folderKey={folder.key} className="inline-block">
                                <div 
                                    className="flex items-center gap-2 cursor-pointer hover:underline py-1 px-2 rounded"
                                    onClick={() => handleNavigate(folder.key)}
                                >
                                    <Folder className="h-4 w-4 text-blue-500" />
                                    {folder.name}
                                </div>
                            </DroppableFolder>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">Folder</TableCell>
                        <TableCell className="hidden md:table-cell">-</TableCell>
                        <TableCell className="hidden lg:table-cell">-</TableCell>
                        <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(folder.key)} className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                        </Button>
                        </TableCell>
                    </TableRow>
                    ))}

                    {/* Files */}
                    {pageFiles.map((file) => (
                    <TableRow key={file.key}>
                        <TableCell>
                            <Checkbox 
                                checked={selectedItems.has(file.key)}
                                onCheckedChange={(checked) => handleSelectionChange(file.key, checked)}
                            />
                        </TableCell>
                        <TableCell className="font-medium">
                            <DraggableItem id={file.key} data={{ type: 'file', key: file.key }}>
                                <div className="flex items-center gap-2 py-1 px-2">
                                    <File className="h-4 w-4 text-gray-500" />
                                    <span 
                                      className="cursor-pointer hover:underline"
                                      onClick={() => handleView(file.key)}
                                    >
                                      {file.name}
                                    </span>
                                </div>
                            </DraggableItem>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">File</TableCell>
                        <TableCell className="hidden md:table-cell">{formatSize(file.size)}</TableCell>
                        <TableCell className="hidden lg:table-cell">{new Date(file.last_modified).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleView(file.key)} title="View/Download">
                              <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(file.key)} className="text-destructive hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        </TableCell>
                    </TableRow>
                    ))}

                    {items.folders.length === 0 && items.files.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        This folder is empty
                        </TableCell>
                    </TableRow>
                    )}
                </TableBody>
                </Table>
                {pageCount > 1 && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
                    <span>
                      Showing {pageStart + 1}–{pageEnd} of {totalItems}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage(safePage - 1)} disabled={safePage === 0}>
                        Previous
                      </Button>
                      <span>Page {safePage + 1} of {pageCount}</span>
                      <Button variant="outline" size="sm" onClick={() => setPage(safePage + 1)} disabled={safePage >= pageCount - 1}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}
                </div>
            )}
            </CardContent>
            
            <DragOverlay>
              {dragActiveItem ? (
                 <div className="bg-white dark:bg-slate-800 p-2 border rounded shadow opacity-90 flex items-center gap-2">
                    <File className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">Moving item...</span>
                 </div>
              ) : null}
            </DragOverlay>
        </Card>
        </div>
    </DndContext>
  );
};

export default FileManager;
